/* eslint-disable import/prefer-default-export */
/* eslint-disable no-restricted-globals */
import { ExportToCsv } from 'export-to-csv'
import React from 'react'
import Button from '../common/button'
import { ExtColumn, FilterOptions } from '../data.grid/models'
import { DownloadOptions } from './models/download.model'
import {
  DEFAULT_DECIMAL_SEPARATOR,
  DEFAULT_DOWNLOAD_BUTTON_ROLE,
  DEFAULT_DOWNLOAD_BUTTON_TEXT,
  DEFAULT_FIELD_SEPARATOR,
  DEFAULT_FILENAME,
  DEFAULT_QUOTE_STRINGS,
  DEFAULT_SHOW_LABELS,
  DEFAULT_SHOW_TITLE,
  DEFAULT_TITLE,
  DEFAULT_USE_BOM,
  DEFAULT_USE_KEYS_AS_HEADERS,
  DEFAULT_USE_TEXT_FILE
} from './download.const'
import * as styles from './download.styl'
import Icon from '../common/icon'
import cx from 'classnames'
import { isObject } from 'lodash'
import { FilteringOptionsProps } from '../filtering.options'

export interface Props {
  getResolvedState: () => {
    sortedData?: any[]
    allVisibleColumns?: any
  }
  downloadOptions?: DownloadOptions
  columns?: ExtColumn<any>[]
  filterOptions?: FilterOptions
  searchValue?: string
  filterOptionsProps?: FilteringOptionsProps
}

export const DownloadButton: React.FC<Props> = (props: Props) => {
  const {
    downloadOptions,
    searchValue,
    getResolvedState,
    filterOptions,
    filterOptionsProps
  } = props
  const data = getResolvedState().sortedData
  const columns = getResolvedState().allVisibleColumns
  function getColumns(): any {
    return columns.filter(column => column.show && column.Header)
  }
  function getDefaultHeaderMapping() {
    return getColumns().map(header => header.Header)
  }

  function defaultDataMapper(data, headers) {
    const mappedHeaders = getColumns().filter(column => {
      const header =
        column.Header?.props?.column?.title ||
        column.Header?.props?.children[0] ||
        column.Header
      return headers.indexOf(header) > -1
    })
    const mappedData = {}
    for (const header of mappedHeaders) {
      const headerId = header?.id
      const dataForHeader = data[headerId]
      switch (header.columnType) {
        case 'number': {
          if (Array.isArray(dataForHeader)) {
            mappedData[headerId] = dataForHeader.length
          } else {
            mappedData[headerId] = dataForHeader ?? '0'
          }
          break
        }
        case 'text': {
          if (isObject(dataForHeader)) {
            mappedData[headerId] = Object.values(dataForHeader).join(' ')
          } else {
            mappedData[headerId] = dataForHeader ?? 'N/A'
          }
          break
        }
        case 'datetime': {
          if (
            dataForHeader === '0' ||
            dataForHeader === null ||
            dataForHeader === undefined
          ) {
            mappedData[headerId] = 'N/A'
          } else if (!isNaN(dataForHeader)) {
            mappedData[headerId] = new Date(+dataForHeader * 1000).toISOString()
          } else if (dataForHeader) {
            mappedData[headerId] = dataForHeader
          } else {
            mappedData[headerId] = 'N/A'
          }
          break
        }
        default: {
          mappedData[headerId] = data[headerId] ?? 'N/A'
          break
        }
      }
    }
    return mappedData
  }

  function getDefaultFilterFunction(): (d) => boolean {
    if (
      filterOptions &&
      filterOptions.hasObjectTypeFilter &&
      filterOptionsProps
    ) {
      const selectedOption = filterOptionsProps.selectedOption
      const selectedLabel = (selectedOption as any)?.label
      if (selectedLabel === 'ALL') {
        return () => true
      } else {
        const selectedOptionRegex = new RegExp(`${selectedLabel}`, 'i')
        return d => selectedOptionRegex.test(JSON.stringify(d))
      }
    }
    return () => true
  }

  const download = (downloadOptions: DownloadOptions) => {
    const {
      filename = DEFAULT_FILENAME,
      fieldSeparator = DEFAULT_FIELD_SEPARATOR,
      quoteStrings = DEFAULT_QUOTE_STRINGS,
      decimalSeparator = DEFAULT_DECIMAL_SEPARATOR,
      showLabels = DEFAULT_SHOW_LABELS,
      showTitle = DEFAULT_SHOW_TITLE,
      title = DEFAULT_TITLE,
      useTextFile = DEFAULT_USE_TEXT_FILE,
      useBom = DEFAULT_USE_BOM,
      useKeysAsHeaders = DEFAULT_USE_KEYS_AS_HEADERS,
      headers = getDefaultHeaderMapping(),
      reducer = (_a, _b, _c, array) => array,
      dataMapper = defaultDataMapper,
      useSearchFilter = true,
      useDefaultFilenameFormatting = true,
      filterFunction = getDefaultFilterFunction()
    } = downloadOptions
    const hasObjectTypeFilter =
      filterOptions && filterOptions.hasObjectTypeFilter
    const selectedOptionValue =
      filterOptionsProps && (filterOptionsProps.selectedOption as any)?.value
    let formattedFilename = useDefaultFilenameFormatting
      ? `${filename}___${
          (hasObjectTypeFilter && `${selectedOptionValue}___`) || ''
        }${new Date()}`
      : filename
    let csvData: any = data.filter(filterFunction)
    if (useSearchFilter && searchValue) {
      if (useDefaultFilenameFormatting) {
        formattedFilename = `${filename}___${
          (hasObjectTypeFilter && `${selectedOptionValue}___`) || ''
        }${searchValue}___${new Date()}`
      }
      const searchRegex = new RegExp(searchValue, 'i')
      csvData = data.filter(d => searchRegex.test(JSON.stringify(d)))
    }
    csvData = csvData.map(data => dataMapper(data, headers)).reduce(reducer, [])
    if (csvData.length === 0) {
      return
    }

    const options = {
      filename: formattedFilename,
      fieldSeparator,
      quoteStrings,
      decimalSeparator,
      showLabels,
      showTitle,
      title,
      useTextFile,
      useBom,
      useKeysAsHeaders,
      headers
    }

    const csvExporter = new ExportToCsv(options)

    csvExporter.generateCsv(csvData)
  }

  return (
    <div className={styles.detailsButton}>
      <Button
        role={
          downloadOptions?.buttonOptions?.buttonRole ||
          DEFAULT_DOWNLOAD_BUTTON_ROLE
        }
        onClick={() => download(downloadOptions)}
        title={
          downloadOptions?.buttonOptions?.buttonText ||
          DEFAULT_DOWNLOAD_BUTTON_TEXT
        }
      >
        <Icon
          className={cx(styles.actionButton, styles.downloadBtn)}
          name="arrow-to-bottom"
        />
      </Button>
    </div>
  )
}
